import type { Database, ObjectShape, Record as SdkRecord, Shape } from '@servicenow/sdk-build-core'
import { NowIdShape } from '@servicenow/sdk-build-plugins'
import {
    getPlaybookProcessTypeIdentifier,
    PLAYBOOK_DEFAULTS,
    PLAYBOOK_PROCESS_TYPE_TABLE,
} from '../constants/playbook-plugin-constants'

function resolvePlaybookProcessTypeName(record: SdkRecord, database?: Database): string | undefined {
    const processType = record.get('process_type')
    const processTypeReference = processType.ifRecordId()
    if (processTypeReference?.hasPrimaryKey()) {
        return processTypeReference.getPrimaryKey()
    }

    const processTypeSysId = processType.ifString()?.getValue()
    if (!processTypeSysId) {
        return undefined
    }

    return (
        getPlaybookProcessTypeIdentifier(processTypeSysId) ??
        database
            ?.query(PLAYBOOK_PROCESS_TYPE_TABLE)
            .find((processTypeRecord) => processTypeRecord.getId().getValue() === processTypeSysId)
            ?.get('name')
            ?.ifString()
            ?.getValue()
    )
}

/**
 * Build the playbook config shape from a database record (toShape direction).
 * Maps snake_case DB fields → camelCase Fluent properties.
 */
export function buildPlaybookConfigToShape(record: SdkRecord, database?: Database): ObjectShape {
    return record.transform(({ $ }) => ({
        $id: $.val(NowIdShape.from(record)),
        label: $.from('label'),
        name: $.from('name'),
        description: $.from('description'),
        active: $.from('active').toBoolean().def(PLAYBOOK_DEFAULTS.ACTIVE),
        status: $.from('status').def(PLAYBOOK_DEFAULTS.STATUS),
        schemaVersion: $.from('schema_version').toNumber().def(PLAYBOOK_DEFAULTS.MAX_SUPPORTED_SCHEMA_VERSION),
        restartable: $.from('restartable').def(PLAYBOOK_DEFAULTS.RESTARTABLE),
        access: $.from('access').def(PLAYBOOK_DEFAULTS.ACCESS),
        runStrategy: $.from('run_strategy').def(PLAYBOOK_DEFAULTS.RUN_STRATEGY),
        processType: $.val(resolvePlaybookProcessTypeName(record, database)).def(PLAYBOOK_DEFAULTS.PROCESS_TYPE),
        allowAsNested: $.from('allow_as_nested').toBoolean().def(PLAYBOOK_DEFAULTS.ALLOW_AS_NESTED),
        executionType: $.from('execution_type').def(PLAYBOOK_DEFAULTS.EXECUTION_TYPE),
        dataRetentionPeriodOverride: $.from('data_retention_period_override').def(
            PLAYBOOK_DEFAULTS.DATA_RETENTION_PERIOD_OVERRIDE
        ),
        snapshot: $.from('snapshot').def(''),
        designerState: $.from('designer_state').def(''),
        nowAssistPrompt: $.from('now_assist_prompt').def(''),
        nowAssistKb: $.from('now_assist_kb').def(''),
        copiedFrom: $.from('copied_from').def(''),
        copiedFromLabel: $.from('copied_from_label').def(''),
    }))
}

/**
 * Build the playbook record properties from a Fluent config shape (toRecord direction).
 * Maps camelCase Fluent properties → snake_case DB fields.
 *
 * Returns the config.transform() result ready for factory.createRecord().
 */
export function buildPlaybookRecordProperties(
    config: ObjectShape,
    computedName: string | undefined,
    nameProvided: boolean,
    resolvedProcessType: string | Shape
): ReturnType<ObjectShape['transform']> {
    return config.transform(({ $ }) => ({
        // User-configurable fields
        label: $,
        // Use explicit name if provided, otherwise use computed name from label
        name: nameProvided ? $ : $.val(computedName),
        description: $.from('description').def(''),
        restartable: $.from('restartable').def(PLAYBOOK_DEFAULTS.RESTARTABLE),
        run_strategy: $.from('runStrategy').def(PLAYBOOK_DEFAULTS.RUN_STRATEGY),
        process_type: $.val(resolvedProcessType),
        allow_as_nested: $.from('allowAsNested').def(PLAYBOOK_DEFAULTS.ALLOW_AS_NESTED),
        execution_type: $.from('executionType').def(PLAYBOOK_DEFAULTS.EXECUTION_TYPE),
        run_strategy_on_process_definition: $.def(PLAYBOOK_DEFAULTS.RUN_STRATEGY_ON_PROCESS_DEFINITION),
        access: $.from('access').def(PLAYBOOK_DEFAULTS.ACCESS),
        permission: $.def(PLAYBOOK_DEFAULTS.PERMISSION),
        public_access: $.def(false),
        start_non_blocking: $.def(PLAYBOOK_DEFAULTS.START_NON_BLOCKING),
        run_on_pad_engine: $.def(false),
        data_retention_period_override: $.from('dataRetentionPeriodOverride').def(
            PLAYBOOK_DEFAULTS.DATA_RETENTION_PERIOD_OVERRIDE
        ),
        snapshot: $.from('snapshot').def(''),
        designer_state: $.from('designerState').def(''),
        now_assist_prompt: $.from('nowAssistPrompt').def(''),
        now_assist_kb: $.from('nowAssistKb').def(''),
        copied_from: $.from('copiedFrom').def(''),
        copied_from_label: $.from('copiedFromLabel').def(''),
        // Hidden fields - always use defaults
        view_type: $.def(PLAYBOOK_DEFAULTS.VIEW_TYPE),
        engine_major_version: $.def(''),
        schema_version: $.from('schemaVersion').def(PLAYBOOK_DEFAULTS.MAX_SUPPORTED_SCHEMA_VERSION),
        active: $.from('active').def(PLAYBOOK_DEFAULTS.ACTIVE),
        status: $.from('status').def(PLAYBOOK_DEFAULTS.STATUS),
    }))
}
